/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import React from 'react';

import { IGetGdnInvoiceDetailsResponse } from '../../../actions/DataServiceEntities.g';
import { formatShortDate, formatAmount } from '../gdn-invoice-details';

export interface IInvoiceDetailsPaymentDetailsData {
    gdnInvoiceDetails: IGetGdnInvoiceDetailsResponse;
}

export interface IInvoiceDetailsPaymentDetailsResources {
    detachText: string;
    billToSummaryLabel: string;
    invoiceSummaryLabel: string;
    orderSummaryLabel: string;
    totalPiecesSummaryLabel: string;
    termsSummaryLabel: string;
    invoiceAmountDueText: string;
    invoicePaymentDueDateText: string;
    invoicePaymentSubtotal: string;
    invoicePaymentTax: string;
    invoicePaymentInvoiceTotal: string;
}

export interface IInvoiceDetailsPaymentDetailsProps extends IComponentProps<IInvoiceDetailsPaymentDetailsData> {
    context: ICoreContext;
    className: string;
    currencyCode: string;
    resources: IInvoiceDetailsPaymentDetailsResources;
}

export interface IInvoiceDetailsPaymentDetailsComponent extends IComponent<IInvoiceDetailsPaymentDetailsProps> {}

interface IInvoicePaymentLineProps {
    label: string;
    value: number;
    className: string;
}

const InvoiceDetailsPaymentDetails: React.FC<IInvoiceDetailsPaymentDetailsProps> = (
    props: IInvoiceDetailsPaymentDetailsProps
): JSX.Element | null => {
    const {
        className,
        context,
        data: { gdnInvoiceDetails },
        resources,
        currencyCode
    } = props;

    const {
        detachText,
        billToSummaryLabel,
        invoiceSummaryLabel,
        orderSummaryLabel,
        totalPiecesSummaryLabel,
        termsSummaryLabel,
        invoicePaymentInvoiceTotal,
        invoiceAmountDueText,
        invoicePaymentDueDateText
    } = resources;

    const invoiceDetails = gdnInvoiceDetails.InvoiceDetails;

    const lineClassName = `${className}__line`;

    const lines: IInvoicePaymentLineProps[] = [];
    if (!ObjectExtensions.isNullOrUndefined(invoiceDetails?.SubTotal)) {
        lines.push({
            label: resources.invoicePaymentSubtotal,
            value: invoiceDetails?.SubTotal || 0,
            className: `${lineClassName}-subtotal`
        });
    }
    if (!ObjectExtensions.isNullOrUndefined(invoiceDetails?.Tax)) {
        lines.push({
            label: resources.invoicePaymentTax,
            value: invoiceDetails?.Tax || 0,
            className: `${lineClassName}-total-tax`
        });
    }

    if (!ArrayExtensions.hasElements(lines)) {
        return null;
    }

    return (
        <div className={className}>
            <div className={`${className}__detach`}>{detachText}</div>
            {lines.map(line => {
                return (
                    <div className={classnames(line.className, lineClassName)} key={line.label}>
                        <div className={`${lineClassName}__label`}>{line.label}</div>
                        <div className={`${lineClassName}__value`}>
                            {props.context.cultureFormatter.formatCurrency(line.value, currencyCode)}
                        </div>
                    </div>
                );
            })}
            <div className={`${className}__summary`}>
                <div className={`${className}__summary--bill-to`}>{`${billToSummaryLabel} ${invoiceDetails?.BillToAccount}`}</div>
                <div className={`${className}__summary--invoice`}>{`${invoiceSummaryLabel} ${invoiceDetails?.InvoiceNumber}`}</div>
                <div className={`${className}__summary--order`}>{`${orderSummaryLabel} ${invoiceDetails?.SalesOrderNumber}`}</div>
                <div className={`${className}__summary--total-pieces`}>{`${totalPiecesSummaryLabel} ${invoiceDetails?.TotalPieces}`}</div>
                <div className={`${className}__summary--invoice-total`}>
                    {`${invoicePaymentInvoiceTotal} ${formatAmount(context, invoiceDetails?.InvoiceAmount, currencyCode)}`}
                </div>
            </div>
            <div className={`${className}__payment-due-details`}>
                <div className={`${className}__payment-due-details__terms`}>
                    <div className={`${className}__payment-due-details__terms--label`}>{termsSummaryLabel}</div>
                    <div className={`${className}__payment-due-details__terms--info`}>{invoiceDetails?.Terms}</div>
                </div>
                <div className={`${className}__payment-due-details__due`}>
                    <div className={`${className}__payment-due-details__due--amount`}>
                        {/* {`${invoiceAmountDueText} ${formatAmount(context, invoiceDetails?.InvoiceAmountMst, currencyCode)}`} */}
                        {`${invoiceAmountDueText} ${formatAmount(context, invoiceDetails?.AmountDue, currencyCode)}`}
                    </div>
                    <div className={`${className}__payment-due-details__due--invoice`}>
                        <div className={`${className}__payment-due-details__due--invoice-label`}>{invoicePaymentDueDateText}</div>
                        <div className={`${className}__payment-due-details__due--invoice-info`}>
                            {formatShortDate(context, invoiceDetails?.DueDate)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const InvoiceDetailsPaymentDetailsComponent: React.FunctionComponent<IInvoiceDetailsPaymentDetailsProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IInvoiceDetailsPaymentDetailsComponent
>('InvoiceDetailsPaymentDetailsComponent', { component: InvoiceDetailsPaymentDetails });
